import { useState, useRef, useEffect } from 'react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const openedAtRef = useRef(0);

  // Time-based spam check: enable submit after 3 seconds
  useEffect(() => {
    if (!isOpen) return;
    openedAtRef.current = Date.now();
    setCanSubmit(false);
    const timer = setTimeout(() => setCanSubmit(true), 3000);
    return () => clearTimeout(timer);
  }, [isOpen]);

  if (!isOpen) return null;

  function handleClose() {
    setEmail('');
    setMessage('');
    setHoneypot('');
    setError(null);
    setIsSuccess(false);
    openedAtRef.current = 0;
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const trimmedMessage = message.trim();
    if (trimmedMessage.length < 10) {
      setError('Please write at least 10 characters.');
      return;
    }
    if (email && !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const endpoint = import.meta.env.VITE_FORMSPREE_ENDPOINT;
    if (!endpoint) {
      setError('Contact form is not configured.');
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await fetch(`https://formspree.io/f/${endpoint}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim() || undefined,
          message: trimmedMessage,
          _gotcha: honeypot,
        }),
      });

      if (res.ok) {
        setIsSuccess(true);
      } else {
        const data = await res.json().catch(() => null);
        setError(data?.error || 'Failed to send message. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const isMessageValid = message.trim().length >= 10;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl bg-purple-card border border-white/10 shadow-2xl overflow-hidden">
        <div className="px-6 pt-6 pb-6 space-y-4">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {isSuccess ? (
            <div className="text-center space-y-3 py-4">
              <div className="text-4xl">💌</div>
              <h2 className="text-xl font-bold text-white">Message sent!</h2>
              <p className="text-sm text-slate-400">
                Thanks for reaching out.
                {email ? " We'll get back to you soon." : ''}
              </p>
              <button
                onClick={handleClose}
                className="w-full py-3 px-6 btn-glass text-white font-semibold rounded-2xl transition-all"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="text-center space-y-1">
                <div className="text-3xl">💬</div>
                <h2 className="text-xl font-bold text-white">Contact Us</h2>
                <p className="text-sm text-slate-400">
                  We'd love to hear from you! Share any questions or suggestions.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot (hidden from humans) */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input
                    id="contact-website"
                    name="_gotcha"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>

                {/* Email (optional) */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400 ml-1">
                    Email <span className="font-normal normal-case">(optional)</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    placeholder="your@email.com"
                    maxLength={320}
                    className="w-full px-4 py-3.5 rounded-2xl text-base outline-none onboarding-input transition-all"
                  />
                </div>

                {/* Message (required) */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400 ml-1">
                    Message
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => { setMessage(e.target.value); setError(null); }}
                    placeholder="Your question or suggestion..."
                    maxLength={2000}
                    rows={4}
                    className="w-full px-4 py-3.5 rounded-2xl text-base outline-none onboarding-input transition-all resize-none"
                  />
                  <p className="text-xs text-slate-500 text-right">{message.length}/2000</p>
                </div>

                {/* Error */}
                {error && (
                  <div className="p-3 rounded-xl bg-red-500/10 border border-red-400/40">
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting || !isMessageValid || !canSubmit}
                  className="w-full py-3 px-6 gradient-cta disabled:opacity-50 text-white font-semibold rounded-xl transition-all"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {/* Cancel */}
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full text-sm text-slate-400 hover:text-white transition-colors text-center"
                >
                  Cancel
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
